import CanvasOverlay from "../assets/img/caricature/canvas transparent.png";

export type ArtworkImageFit = "contain" | "cover" | "stretch" | "height" | "frame";
export type ArtworkLayer = "above-product" | "below-product";
/**
 * "object" measures the print area against the detected product inside the photo,
 * which keeps artwork the same relative size across mockups with different padding.
 * "canvas" measures against the whole canvas (used for full-bleed templates).
 */
export type ArtworkAreaReference = "object" | "canvas" | "product";

export interface ArtworkPlacement {
  /** Canvas width divided by height. */
  canvasAspectRatio: number;
  /** Normalized print area (0–1) relative to `areaRelativeTo`. */
  area: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  areaRelativeTo: ArtworkAreaReference;
  productFit: ArtworkImageFit;
  artworkFit: ArtworkImageFit;
  artworkLayer: ArtworkLayer;
  artworkOpacity: number;
  artworkRotation: number;
  productCompositeOperation: GlobalCompositeOperation;
  templateImage?: string;
  /** When set, square art is scaled by this. */
  artworkSheetScale?: number;
  /** When set, landscape art is scaled by this. */
  artworkLandscapeScale?: number;
}

const DEFAULT_PLACEMENT: ArtworkPlacement = {
  canvasAspectRatio: 1,
  area: { x: 0.22, y: 0.24, width: 0.56, height: 0.52 },
  areaRelativeTo: "object",
  productFit: "contain",
  artworkFit: "contain",
  artworkLayer: "above-product",
  artworkOpacity: 0.98,
  artworkRotation: 0,
  productCompositeOperation: "source-over",
};

/** T-shirt chest print — centered the same for every color (black/white). */
const TSHIRT_ART_WIDTH = 0.56 * 0.8;
const TSHIRT_ART_HEIGHT = 0.44 * 0.8;
const TSHIRT_PLACEMENT: Partial<ArtworkPlacement> = {
  area: {
    x: (1 - TSHIRT_ART_WIDTH) / 2,
    y: 0.24,
    width: TSHIRT_ART_WIDTH,
    height: TSHIRT_ART_HEIGHT,
  },
  areaRelativeTo: "product",
  artworkLayer: "above-product",
  productCompositeOperation: "source-over",
  /** Landscape only — square uses default sheet scale (unchanged). */
  artworkLandscapeScale: 0.92,
};

/** Same chest print for every hoodie color (black/white). */
const HOODIE_ART_WIDTH = 0.56 * 0.9 * 0.92 * 0.9;
const HOODIE_ART_HEIGHT = 0.52 * 0.9 * 0.92 * 0.9;
const HOODIE_PLACEMENT: Partial<ArtworkPlacement> = {
  area: {
    x: (1 - HOODIE_ART_WIDTH) / 2,
    y: 0.24 + (0.52 - HOODIE_ART_HEIGHT) / 2,
    width: HOODIE_ART_WIDTH,
    height: HOODIE_ART_HEIGHT,
  },
  areaRelativeTo: "product",
  artworkLayer: "above-product",
  productCompositeOperation: "source-over",
};

const PHOTO_PAPER_SQUARE_ART_SCALE = 0.78 * 0.75;
const POSTER_SQUARE_ART_SCALE = PHOTO_PAPER_SQUARE_ART_SCALE * 1.2 * 1.1 * 1.05 * 1.05 * 1.02;
/** Landscape print size is locked. Square tweaks must not change these. */
const POSTER_LANDSCAPE_ART_SCALE = 0.912;
const PHOTO_PAPER_LANDSCAPE_ART_SCALE = 0.89376;

/** Poster sheet is landscape 18×12 in a square mockup. */
const POSTER_SHEET_WIDTH = 0.86;
const POSTER_SHEET_HEIGHT = POSTER_SHEET_WIDTH / (18 / 12);
const POSTER_FRAME_FILL: Partial<ArtworkPlacement> = {
  area: {
    x: (1 - POSTER_SHEET_WIDTH) / 2,
    y: (1 - POSTER_SHEET_HEIGHT) / 2,
    width: POSTER_SHEET_WIDTH,
    height: POSTER_SHEET_HEIGHT,
  },
  areaRelativeTo: "product",
  artworkFit: "contain",
  artworkOpacity: 1,
  artworkSheetScale: POSTER_SQUARE_ART_SCALE,
  artworkLandscapeScale: POSTER_LANDSCAPE_ART_SCALE,
};

const PHOTO_PAPER_INNER_SIZE = 0.86;
const PHOTO_PAPER_FRAME_FILL: Partial<ArtworkPlacement> = {
  area: {
    x: (1 - PHOTO_PAPER_INNER_SIZE) / 2,
    y: (1 - PHOTO_PAPER_INNER_SIZE) / 2,
    width: PHOTO_PAPER_INNER_SIZE,
    height: PHOTO_PAPER_INNER_SIZE,
  },
  areaRelativeTo: "product",
  artworkFit: "contain",
  artworkOpacity: 1,
  artworkSheetScale: PHOTO_PAPER_SQUARE_ART_SCALE,
  artworkLandscapeScale: PHOTO_PAPER_LANDSCAPE_ART_SCALE,
};
/**
 * New products should provide `artwork_placement` (normalized 0–1 values)
 * from the API instead of adding another rendering component.
 */
const SKU_PLACEMENTS: Record<string, Partial<ArtworkPlacement>> = {
  ts: TSHIRT_PLACEMENT,
  hd: HOODIE_PLACEMENT,
  cp: {
    area: { x: 0.23, y: 0.22, width: 0.54, height: 0.34 },
    areaRelativeTo: "product",
    artworkLayer: "above-product",
    productCompositeOperation: "source-over",
  },
  // Handles are part of the detected object, so the print area sits lower.
  bg: {
    area: { x: 0.14, y: 0.37, width: 0.72, height: 0.42 },
  },
  mg: {
    area: { x: 0.24, y: 0.28, width: 0.52, height: 0.42 },
  },
  ap: {
    area: { x: 0.22, y: 0.28, width: 0.56, height: 0.48 },
  },
  mp: {
    area: { x: 0.1, y: 0.12, width: 0.8, height: 0.76 },
  },
  tb: {
    area: { x: 0.14, y: 0.32, width: 0.72, height: 0.42 },
    artworkSheetScale: 1.5,
  },
  ab: {
    area: { x: 0.21, y: 0.23, width: 0.58, height: 0.54 },
  },
  st: {
    area: { x: 0.03, y: 0.03, width: 0.94, height: 0.94 },
  },
  pp: PHOTO_PAPER_FRAME_FILL,
  poster: POSTER_FRAME_FILL,
  ca: {
    canvasAspectRatio: 375 / 230,
    area: { x: 0, y: 0, width: 1, height: 1 },
    areaRelativeTo: "canvas",
    artworkLayer: "below-product",
    artworkOpacity: 1,
    productCompositeOperation: "multiply",
    templateImage: CanvasOverlay,
  },
  rm: {
    canvasAspectRatio: 375 / 230,
    area: { x: 0, y: 0, width: 1, height: 1 },
    areaRelativeTo: "canvas",
    artworkLayer: "below-product",
    artworkOpacity: 1,
    productCompositeOperation: "multiply",
    templateImage: CanvasOverlay,
  },
};

/** Products whose photo is already the printed artwork must not be overlaid again. */
const ARTWORK_DISABLED_KEYWORDS = ["sticker"];

export function isArtworkDisabledForProduct(productName?: string | null): boolean {
  const name = String(productName || "").toLowerCase();
  if (!name) return false;
  return ARTWORK_DISABLED_KEYWORDS.some((keyword) => name.includes(keyword));
}

function finiteNumber(value: unknown, fallback: number): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizedNumber(value: unknown, fallback: number): number {
  return Math.min(1, Math.max(0, finiteNumber(value, fallback)));
}

function readArea(source: any, fallback: ArtworkPlacement["area"]): ArtworkPlacement["area"] {
  const area = source?.area || source?.print_area || source?.printArea || source || {};
  const width = normalizedNumber(area.width ?? area.w, fallback.width);
  const height = normalizedNumber(area.height ?? area.h, fallback.height);
  const x = Math.min(1 - width, normalizedNumber(area.x ?? area.left, fallback.x));
  const y = Math.min(1 - height, normalizedNumber(area.y ?? area.top, fallback.y));
  return { x, y, width, height };
}

function productHaystack(productName?: string | null, skuCode?: string | null): string {
  let name = "";
  if (typeof productName === "string") {
    name = productName;
  } else if (productName && typeof productName === "object") {
    const nested = (productName as { name?: unknown }).name;
    name = typeof nested === "string" ? nested : String(nested || "");
  }
  return `${name} ${String(skuCode || "")}`.toLowerCase();
}

function isPosterProduct(productName?: string | null, skuCode?: string | null): boolean {
  const sku = String(skuCode || "").trim().toLowerCase();
  const haystack = productHaystack(productName, skuCode);
  if (
    haystack.includes("photo paper") ||
    haystack.includes("photopaper") ||
    haystack.includes("photo-paper") ||
    haystack.includes("photo print")
  ) {
    return false;
  }
  return haystack.includes("poster") || sku === "poster" || sku.startsWith("poster");
}

function isPhotoPaperProduct(productName?: string | null, skuCode?: string | null): boolean {
  const sku = String(skuCode || "").trim().toLowerCase();
  const haystack = productHaystack(productName, skuCode);
  if (
    haystack.includes("photo paper") ||
    haystack.includes("photopaper") ||
    haystack.includes("photo-paper") ||
    haystack.includes("photo print")
  ) {
    return true;
  }
  if (isPosterProduct(productName, skuCode)) return false;
  return sku === "pp" || sku.startsWith("pp");
}

function isPosterOrPhotoPaperProduct(
  productName?: string | null,
  skuCode?: string | null
): boolean {
  return isPosterProduct(productName, skuCode) || isPhotoPaperProduct(productName, skuCode);
}

function scaleAreaKeepCenter(
  area: ArtworkPlacement["area"],
  factor: number
): ArtworkPlacement["area"] {
  const width = area.width * factor;
  const height = area.height * factor;
  return {
    x: area.x + (area.width - width) / 2,
    y: area.y + (area.height - height) / 2,
    width,
    height,
  };
}

function isCapProduct(productName?: string | null, skuCode?: string | null): boolean {
  const sku = String(skuCode || "").trim().toLowerCase();
  const haystack = productHaystack(productName, skuCode);
  return /\bcap\b/.test(haystack) || sku === "cp" || sku.startsWith("cp");
}

function isHoodieProduct(productName?: string | null, skuCode?: string | null): boolean {
  const sku = String(skuCode || "").trim().toLowerCase();
  const haystack = productHaystack(productName, skuCode);
  return (
    haystack.includes("hoodie") ||
    haystack.includes("hood") ||
    sku === "hd" ||
    sku.startsWith("hd")
  );
}

function isTshirtSku(skuCode?: string | null): boolean {
  const sku = String(skuCode || "").trim().toLowerCase();
  if (!sku) return false;
  return (
    sku === "ts" ||
    sku.startsWith("ts") ||
    sku.includes("tshirt") ||
    sku.includes("t-shirt")
  );
}

function isToteBagProduct(productName?: string | null, skuCode?: string | null): boolean {
  const sku = String(skuCode || "").trim().toLowerCase();
  const haystack = productHaystack(productName, skuCode);
  return haystack.includes("tote") || sku === "tb" || sku.startsWith("tb");
}

/** Soft goods: art must sit above the garment. Multiply-under makes dark colors look solid black. */
function isApparelProduct(productName?: string | null, skuCode?: string | null): boolean {
  return (
    isTshirtSku(skuCode) ||
    isHoodieProduct(productName, skuCode) ||
    isToteBagProduct(productName, skuCode) ||
    isCapProduct(productName, skuCode)
  );
}

function resolveSkuPreset(skuCode?: string | null): Partial<ArtworkPlacement> {
  const sku = String(skuCode || "").trim().toLowerCase();
  if (!sku) return {};

  if (SKU_PLACEMENTS[sku]) return SKU_PLACEMENTS[sku];

  // Prefer longest prefix match so values like "ts001" / "mg-white" still resolve.
  const matchedKey = Object.keys(SKU_PLACEMENTS)
    .filter((key) => sku.startsWith(key))
    .sort((a, b) => b.length - a.length)[0];

  return matchedKey ? SKU_PLACEMENTS[matchedKey] : {};
}

export function resolveArtworkPlacement(
  skuCode?: string | null,
  apiPlacement?: unknown,
  productName?: string | null
): ArtworkPlacement {
  const apparelPreset = isTshirtSku(skuCode)
    ? TSHIRT_PLACEMENT
    : isHoodieProduct(productName, skuCode)
      ? HOODIE_PLACEMENT
      : null;
  const frameFill = isPosterProduct(productName, skuCode)
    ? POSTER_FRAME_FILL
    : isPhotoPaperProduct(productName, skuCode)
      ? PHOTO_PAPER_FRAME_FILL
      : apparelPreset;
  const preset = frameFill
    ? { ...resolveSkuPreset(skuCode), ...frameFill }
    : resolveSkuPreset(skuCode);
  const base: ArtworkPlacement = {
    ...DEFAULT_PLACEMENT,
    ...preset,
    area: preset.area || DEFAULT_PLACEMENT.area,
  };
  const isCap = isCapProduct(productName, skuCode);
  const isApparel = isApparelProduct(productName, skuCode);

  const withApparelSafeLayer = (placement: ArtworkPlacement): ArtworkPlacement =>
    isApparel
      ? {
          ...placement,
          // Same print box for black/white — don't use object detect (differs by garment color).
          areaRelativeTo: placement.areaRelativeTo === "canvas" ? "canvas" : "product",
          artworkLayer: "above-product",
          productCompositeOperation: "source-over",
          templateImage: undefined,
        }
      : placement;

  if (isPosterOrPhotoPaperProduct(productName, skuCode) || isHoodieProduct(productName, skuCode) || isTshirtSku(skuCode)) {
    return withApparelSafeLayer(base);
  }

  if (!apiPlacement || typeof apiPlacement !== "object") {
    const withoutApi = isCap ? { ...base, area: scaleAreaKeepCenter(base.area, 0.96) } : base;
    return withApparelSafeLayer(withoutApi);
  }

  const value = apiPlacement as any;
  const productFit = value.productFit ?? value.product_fit;
  const artworkFit = value.artworkFit ?? value.artwork_fit ?? value.fit;
  const artworkLayer = value.artworkLayer ?? value.artwork_layer ?? value.layer;
  const areaRelativeTo = value.areaRelativeTo ?? value.area_relative_to;
  const resolvedArea =
    isTshirtSku(skuCode) || isToteBagProduct(productName, skuCode)
      ? base.area
      : readArea(value, base.area);

  return withApparelSafeLayer({
    ...base,
    canvasAspectRatio: Math.max(
      0.1,
      finiteNumber(value.canvasAspectRatio ?? value.canvas_aspect_ratio, base.canvasAspectRatio)
    ),
    area: isCap ? scaleAreaKeepCenter(resolvedArea, 0.96) : resolvedArea,
    areaRelativeTo: areaRelativeTo === "canvas" ? "canvas" : base.areaRelativeTo,
    productFit:
      productFit === "cover" || productFit === "stretch" ? productFit : base.productFit,
    artworkFit:
      artworkFit === "cover" || artworkFit === "stretch" ? artworkFit : base.artworkFit,
    artworkLayer: artworkLayer === "below-product" ? artworkLayer : base.artworkLayer,
    artworkOpacity: normalizedNumber(
      value.artworkOpacity ?? value.artwork_opacity ?? value.opacity,
      base.artworkOpacity
    ),
    artworkRotation: finiteNumber(
      value.artworkRotation ?? value.artwork_rotation ?? value.rotation,
      base.artworkRotation
    ),
    productCompositeOperation:
      value.productCompositeOperation ??
      value.product_composite_operation ??
      base.productCompositeOperation,
    templateImage: value.templateImage ?? value.template_image ?? base.templateImage,
  });
}

